import React, { useState } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { login } from '@/lib/adminApi';
import { useAdminAuth } from './AdminAuthContext';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { admin, loading, refresh } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!loading && admin) {
    const to = location.state?.from || '/founder-command';
    return <Navigate to={to} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      await refresh();
      navigate('/founder-command');
    } catch (err) {
      if (err?.response?.status === 429) {
        setError('Too many attempts. Please wait a minute.');
      } else if (err?.response?.status === 401) {
        setError('Invalid credentials.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{
        background:
          'radial-gradient(1200px 700px at 30% 20%, #F6EFE4 0%, #FAF8F5 35%, #F5F2ED 100%)',
      }}
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-10">
          <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-4xl tracking-tight text-neutral-900">
            TOIA <em style={{ color: '#C8A15A', fontStyle: 'italic' }}>Wallace</em>
          </div>
          <div className="h-px w-10 bg-[#C8A15A] mx-auto my-5" />
          <div className="uppercase tracking-[0.32em] text-[10px] text-neutral-500">
            Founder Command
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/80 backdrop-blur border border-neutral-200/60 rounded-2xl p-8 shadow-[0_8px_40px_rgba(17,17,17,0.06)]"
        >
          <label className="block text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-2">
            Email
          </label>
          <input
            data-testid="admin-login-email-input"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-6 px-0 py-2 border-0 border-b border-neutral-300 bg-transparent focus:border-neutral-900 focus:outline-none text-neutral-900 placeholder-neutral-400 text-[15px]"
            placeholder="you@iamtoiawallace.com"
          />

          <label className="block text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-2">
            Password
          </label>
          <input
            data-testid="admin-login-password-input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-8 px-0 py-2 border-0 border-b border-neutral-300 bg-transparent focus:border-neutral-900 focus:outline-none text-neutral-900 placeholder-neutral-400 text-[15px]"
            placeholder="••••••••••"
          />

          {error && (
            <div
              data-testid="admin-login-error"
              className="mb-6 text-[13px] text-red-700 bg-red-50/70 border border-red-100 px-3 py-2 rounded"
            >
              {error}
            </div>
          )}

          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full bg-neutral-900 text-white uppercase tracking-[0.24em] text-[11px] py-4 rounded-full hover:bg-[#1a1a1a] transition-colors disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Enter'}
          </button>
        </form>

        <p className="text-center mt-8 text-[11px] tracking-[0.2em] uppercase text-neutral-400">
          Private · Not linked publicly
        </p>
      </div>
    </div>
  );
}
